import type { ExpressionSpecification, FillLayerSpecification, LineLayerSpecification, Map } from "mapbox-gl"

export const PARCEL_SOURCE_LAYER = "gold.parcels"

export enum ParcelsLayerId {
	Fill = "cadastre",
	Line = "cadastre-line",
}

export enum ParcelsTheme {
	Vector = "vector",
	Satellite = "satellite",
}

const PARCELS_MIN_ZOOM = 13

const isHovered: ExpressionSpecification = ["boolean", ["feature-state", "hover"], false]
const isSelected: ExpressionSpecification = [
	"any",
	["boolean", ["feature-state", "highlight"], false],
	["boolean", ["feature-state", "tourHint"], false],
]

type LineWidthStop = [zoom: number, base: number, emphasized: number]

const VECTOR_LINE_WIDTHS: LineWidthStop[] = [
	[13, 0.3, 1],
	[16, 0.8, 2],
	[18, 1.5, 3],
	[20, 2.5, 4.5],
]

const SATELLITE_LINE_WIDTHS: LineWidthStop[] = [
	[13, 0.5, 1.25],
	[16, 1, 2.25],
	[18, 1.75, 3.25],
	[20, 2.75, 4.75],
]

const SELECTED_HOVER_WIDTH_FACTOR = 1.5

function interpolateLineByZoom(
	widths: LineWidthStop[],
	{ scale, baseScale }: { scale: number; baseScale: number }
): ExpressionSpecification {
	const stops = widths.flatMap(([zoom, base, emphasized]) => [
		zoom,
		[
			"case",
			["all", isSelected, isHovered],
			emphasized * SELECTED_HOVER_WIDTH_FACTOR * scale,
			["any", isSelected, isHovered],
			emphasized * scale,
			base * baseScale,
		],
	])
	return ["interpolate", ["linear"], ["zoom"], ...stops] as ExpressionSpecification
}

function linePaintFor(widths: LineWidthStop[]): LineLayerSpecification["paint"] {
	return {
		"line-width": interpolateLineByZoom(widths, { scale: 1, baseScale: 1 }),
		"line-offset": interpolateLineByZoom(widths, { scale: 0.5, baseScale: 0 }),
	}
}

const PAINT: Record<ParcelsTheme, { fill: FillLayerSpecification["paint"]; line: LineLayerSpecification["paint"] }> = {
	[ParcelsTheme.Vector]: {
		fill: {
			"fill-color": "#0b6e4f",
			"fill-opacity": ["case", isSelected, 0.4, isHovered, 0.1, 0],
		},
		line: {
			...linePaintFor(VECTOR_LINE_WIDTHS),
			"line-color": "#0b6e4f",
		},
	},
	[ParcelsTheme.Satellite]: {
		fill: {
			"fill-color": "#000000",
			"fill-opacity": 0,
		},
		line: {
			...linePaintFor(SATELLITE_LINE_WIDTHS),
			"line-color": ["case", isSelected, "#2ff070", isHovered, "#efc729", "#f8e182"],
		},
	},
}

export function addParcels(
	map: Map,
	{
		baseUrl,
		sourceName,
		layerName,
		visibleLayers,
		theme = ParcelsTheme.Vector,
	}: {
		baseUrl: string
		sourceName: string
		layerName: string
		visibleLayers: string[]
		theme?: ParcelsTheme
	}
): void {
	const visibility = visibleLayers.includes("cadastre") ? "visible" : "none"

	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/${PARCEL_SOURCE_LAYER}/{z}/{x}/{y}.pbf?filter=status='active'`],
		promoteId: "parcel_id",
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": PARCEL_SOURCE_LAYER,
		slot: "top",
		type: "fill",
		layout: { visibility },
		paint: PAINT[theme].fill,
		minzoom: PARCELS_MIN_ZOOM,
		metadata: { custom: true },
	})
	map.addLayer({
		id: `${layerName}-line`,
		source: sourceName,
		"source-layer": PARCEL_SOURCE_LAYER,
		slot: "top",
		type: "line",
		layout: { visibility },
		paint: PAINT[theme].line,
		minzoom: PARCELS_MIN_ZOOM,
		metadata: { custom: true },
	})
}
